import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import type { RefillBonusTierDto } from '@gc/shared';

import { Field, Section, ghostPillClass, inputClass } from './controls';

/**
 * "Loyalty & Cashback" (wave 3, item #19) — the three venue rates plus the
 * refill-bonus tier table, extracted as a presentational component so the
 * render suite can pin its one hard rule:
 *
 * EVERY RATE ALWAYS SHOWS A PLAIN-LANGUAGE PREVIEW LINE. A zero shows the
 * "disabled" wording — it never hides — because the difference between "0
 * points per hour" and a field someone forgot is precisely what the owner
 * cannot see from a bare number input.
 *
 * The tier preview mirrors `resolveRefillBonus` semantics in words: highest
 * matching tier wins outright, flat percent is the fallback. The maths lives
 * in `@gc/shared`; this file only SAYS what will happen.
 */

export type LoyaltyRateKey =
  'pointsPerHourPlayed' | 'pointsPerThousandIqd' | 'refillBonusFlatPercent';

export interface LoyaltySectionProps {
  /** Anchor id for the settings search; see `Section` in controls.tsx. */
  id?: string;
  pointsPerHourPlayed: number;
  pointsPerThousandIqd: number;
  refillBonusFlatPercent: number;
  /** Already resolved via `resolvePointsName` — a brand name or the reader's word. */
  pointsName: string;
  currencySymbol: string;
  /** Null while loading or when the list failed — the table shows its own state. */
  tiers: RefillBonusTierDto[] | null;
  tiersError: string | null;
  tierBusy: boolean;
  onRateChange: (key: LoyaltyRateKey, value: number) => void;
  onAddTier: (minAmountIqd: number, bonusPercent: number) => void;
  onDeleteTier: (tier: RefillBonusTierDto) => void;
  onToggleTier: (tier: RefillBonusTierDto, isActive: boolean) => void;
  errorFor: (field: string) => string | undefined;
}

/** Millidinars (decimal string) -> whole IQD, formatted with latin digits. */
function tierAmountIqd(minAmountMd: string, locale: string): string {
  const iqd = BigInt(minAmountMd || '0') / 1000n;
  return new Intl.NumberFormat(locale, { numberingSystem: 'latn' }).format(iqd);
}

/** Clamp a rate input to a whole, non-negative number; garbage becomes 0. */
function toRate(raw: string): number {
  const value = Math.trunc(Number(raw));
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function LoyaltySection({
  id,
  pointsPerHourPlayed,
  pointsPerThousandIqd,
  refillBonusFlatPercent,
  pointsName,
  currencySymbol,
  tiers,
  tiersError,
  tierBusy,
  onRateChange,
  onAddTier,
  onDeleteTier,
  onToggleTier,
  errorFor,
}: LoyaltySectionProps) {
  const { t, i18n } = useTranslation();

  const [draftAmount, setDraftAmount] = useState('');
  const [draftPercent, setDraftPercent] = useState('');
  const draftValid =
    Number.isInteger(Number(draftAmount)) &&
    Number(draftAmount) > 0 &&
    Number.isInteger(Number(draftPercent)) &&
    Number(draftPercent) >= 1 &&
    Number(draftPercent) <= 100;

  function submitDraft(): void {
    if (!draftValid) return;
    onAddTier(Number(draftAmount), Number(draftPercent));
    setDraftAmount('');
    setDraftPercent('');
  }

  return (
    <Section id={id} title={t('settings.general.loyaltySection')}>
      <Field
        label={t('settings.general.pointsPerHour')}
        hint={t('settings.general.pointsRatesHint')}
        error={errorFor('pointsPerHourPlayed')}
      >
        <input
          type="number"
          min="0"
          step="1"
          value={pointsPerHourPlayed}
          onChange={(event) => onRateChange('pointsPerHourPlayed', toRate(event.target.value))}
          className={`${inputClass} tabular`}
        />
        {/* The preview never hides: zero READS as disabled, in words. */}
        <p data-preview="pointsPerHour" className="mt-1 text-xs text-content-secondary">
          {pointsPerHourPlayed > 0
            ? t('settings.general.pointsPerHourPreview', {
                count: pointsPerHourPlayed,
                pointsName,
              })
            : t('settings.general.pointsPerHourDisabled', { pointsName })}
        </p>
      </Field>

      <Field
        label={t('settings.general.pointsPerThousand')}
        error={errorFor('pointsPerThousandIqd')}
      >
        <input
          type="number"
          min="0"
          step="1"
          value={pointsPerThousandIqd}
          onChange={(event) => onRateChange('pointsPerThousandIqd', toRate(event.target.value))}
          className={`${inputClass} tabular`}
        />
        <p data-preview="pointsPerThousand" className="mt-1 text-xs text-content-secondary">
          {pointsPerThousandIqd > 0
            ? t('settings.general.pointsPerThousandPreview', {
                count: pointsPerThousandIqd,
                symbol: currencySymbol,
                pointsName,
              })
            : t('settings.general.pointsPerThousandDisabled', { pointsName })}
        </p>
      </Field>

      <Field
        label={t('settings.general.refillFlatPercent')}
        hint={t('settings.general.refillFlatHint')}
        error={errorFor('refillBonusFlatPercent')}
      >
        <input
          type="number"
          min="0"
          max="100"
          step="1"
          value={refillBonusFlatPercent}
          onChange={(event) => onRateChange('refillBonusFlatPercent', toRate(event.target.value))}
          className={`${inputClass} tabular`}
        />
        <p data-preview="refillFlat" className="mt-1 text-xs text-content-secondary">
          {refillBonusFlatPercent > 0
            ? t('settings.general.refillFlatPreview', { percent: refillBonusFlatPercent })
            : t('settings.general.refillFlatDisabled')}
        </p>
      </Field>

      {/* ---- Tier table (printers-list pattern) --------------------------- */}
      <div>
        <span className="mb-1 block text-sm text-content-secondary">
          {t('settings.general.refillTiers')}
        </span>
        <p className="mb-2 text-xs text-content-muted">{t('settings.general.refillTiersHint')}</p>

        {tiersError ? (
          <p role="alert" className="mb-2 text-sm text-danger">
            {tiersError}
          </p>
        ) : null}

        <div className="space-y-2">
          {(tiers ?? []).map((tier) => (
            <div
              key={tier.id}
              data-tier-active={tier.isActive}
              className="flex items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2"
            >
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-sm ${tier.isActive ? 'text-content' : 'text-content-muted line-through'}`}
                >
                  {t('settings.general.tierPreview', {
                    amount: tierAmountIqd(tier.minAmountMd, i18n.language),
                    symbol: currencySymbol,
                    percent: tier.bonusPercent,
                  })}
                </span>
              </span>

              <label className="flex shrink-0 items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={tier.isActive}
                  disabled={tierBusy}
                  onChange={(event) => onToggleTier(tier, event.target.checked)}
                  className="h-4 w-4 accent-[var(--brand-primary)]"
                />
                <span className="text-xs text-content-secondary">
                  {t('settings.general.tierActive')}
                </span>
              </label>

              <button
                type="button"
                disabled={tierBusy}
                onClick={() => onDeleteTier(tier)}
                title={t('common.delete')}
                aria-label={t('common.delete')}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}

          {tiers !== null && tiers.length === 0 ? (
            <p className="text-sm text-content-muted">{t('settings.general.noTiers')}</p>
          ) : null}

          <div className="flex flex-wrap items-end gap-2">
            <Field
              label={t('settings.general.tierMinAmount', { symbol: currencySymbol })}
              error={errorFor('minAmountIqd')}
            >
              <input
                type="number"
                min="1"
                step="1"
                value={draftAmount}
                onChange={(event) => setDraftAmount(event.target.value)}
                className={`${inputClass} w-36 tabular`}
              />
            </Field>
            <Field label={t('settings.general.tierBonusPercent')} error={errorFor('bonusPercent')}>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={draftPercent}
                onChange={(event) => setDraftPercent(event.target.value)}
                className={`${inputClass} w-24 tabular`}
              />
            </Field>
            <button
              type="button"
              disabled={!draftValid || tierBusy}
              onClick={submitDraft}
              className={ghostPillClass}
            >
              <span className="inline-flex items-center gap-1">
                <Plus className="h-4 w-4" aria-hidden />
                {t('settings.general.addTier')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
}
